import pick from 'lodash/pick';
import produce, { current } from 'immer';
import sortBy from 'lodash/sortBy';
import cloneDeep from 'lodash/cloneDeep';
import { tabIds, ACTIONS } from '../utils/constants';
import { updateItemsUsedDetail } from '../utils';

const {
  SET_ACTIVE_TAB,
  SET_IS_DIRTY,
  SET_TRIGGER_SAVE,
  SET_QUIZ_TITLE,
  ADD_QUESTIONS_TO_QUIZ,
  PUSH_QUESTIONS_INTO_QUIZ,
  EMPTY_MY_QUIZ_PENDING,
  RESET_QUIZ,
  SET_NEW_QUIZ,
  SET_MY_QUIZ,
  SET_ACTIVE_STIMULUS_FILTERS,
  SET_LOADED_DATA,
  SET_EDIT_ASSESSMENT_PROPS,
  CLOSE_AND_RESET_QUIZ,
  QUIZ_REVIEW_TAB_LOADING,
  SET_SUBJECTS,
  SAVE_QUIZ,
  UPDATE_ITEM_USAGE,
  UPDATE_ITEM_ASSIGNEES,
  ROLLBACK_QUIZ,
  SET_TEACHER_SECTIONS,
  SET_SUBJECT_INFO,
  TOGGLE_ITEMS_COLUMNS_VISIBLITY,
  MOVE_ITEMS_COLUMN,
  SET_ITEMS_COLUMNS_VISIBLITY,
  RESET_ITEMS_COLUMNS_VISIBLITY,
  SET_LOADING_QUIZ,
} = ACTIONS;

export const reducer = (state, action) => {
  const { type, payload, persistFn } = action;
  const {
    activeTab,
    myQuiz,
    questions,
    stimulusQuestionsInQuiz,
    activeStimulusFilters,
    quizTitle,
    quizId,
    isDirty,
    libraryId,
    itemsColumns,
    items,
    addAllItemIds,
    myQuizIds,
    stimuli,
    stimuliQuestions,
    triggerSave,
    quizReviewTabLoading,
    subjects,
    updateItemUsageCheckAll,
    updateItemUsageRemove,
    teacherSections,
    courseOutlineConfig,
    subjectInfo,
    updatedAssignees,
  } = payload || {};
  let updatedItems;
  let updatedAllStimulusQuestions;

  switch (type) {
    case SET_SUBJECTS:
      return {
        ...state,
        subjects: [...subjects],
      };
    case SET_ACTIVE_TAB:
      return {
        ...state,
        activeTab,
      };
    case SET_MY_QUIZ:
      return {
        ...state,
        myQuiz,
        stimulusQuestionsInQuiz,
        isDirty: !(myQuiz.length === 0),
      };
    case SET_QUIZ_TITLE:
      return {
        ...state,
        quizTitle,
      };
    case SET_IS_DIRTY:
      return {
        ...state,
        isDirty,
      };
    case SET_TRIGGER_SAVE:
      return {
        ...state,
        triggerSave,
      };
    case ADD_QUESTIONS_TO_QUIZ: {
      // we use it to remove question as well
      let newState = {
        ...state,
        libraryId: libraryId || state.libraryId,
        hasMyQuizPending: false,
      };

      if (state.quizId) {
        newState.isDirty = true;
      }

      myQuizIds.forEach((newMyQuizId) => {
        if (!newState.myQuiz.includes(newMyQuizId)) {
          newState.myQuiz.push(newMyQuizId);
          // Since the question was added into the quiz, it no longer has to live in the queue
          delete newState.myQuizPending[newMyQuizId];
        }
      });

      questions.forEach((newQuestion) => {
        if (newQuestion.id && !(newQuestion.id in newState.questions)) {
          newState.questions[newQuestion.id] = newQuestion;
        }
      });

      stimuli.forEach((newStimulus) => {
        if (newStimulus.id) {
          const stimulusQuestions = stimuliQuestions[newStimulus.id].map(
            (q) => q.item_id,
          );
          const newMyQuizStimulusQuestions = questions
            .filter((q) => stimulusQuestions.includes(q.id))
            .map((q) => q.id);

          const existingStimulusInQuiz = state.stimulusQuestionsInQuiz || {};
          newState.stimulusQuestionsInQuiz[newStimulus.id] = [
            ...new Set([
              ...(existingStimulusInQuiz[newStimulus.id] || []),
              ...newMyQuizStimulusQuestions,
            ]),
          ];

          newState.allStimulusQuestions[newStimulus.id] =
            stimuliQuestions[newStimulus.id];
        }
      });

      // Check if any pending item was already added into the quiz
      Object.keys(newState.myQuizPending).forEach((pendingId) => {
        Object.keys(newState.stimulusQuestionsInQuiz).forEach(
          (quizStimulusId) => {
            if (
              newState.stimulusQuestionsInQuiz[quizStimulusId].includes(
                Number(pendingId),
              )
            ) {
              delete newState.myQuizPending[Number(pendingId)];
            }
          },
        );
      });

      newState.myQuizBak = newState.myQuiz.slice();
      newState.stimulusQuestionsInQuizBak = {
        ...newState.stimulusQuestionsInQuiz,
      };
      return newState;
    }
    case PUSH_QUESTIONS_INTO_QUIZ: {
      const newState = Object.assign({}, state);

      (payload || []).forEach((item) => {
        // Do not add the item again into the queue if it's already there
        // (because a new `myQuizPending` object means a new render cycle)
        // In this case `payload` is the question which should be added into the quiz
        if (!newState.myQuizPending[item.item_id]) {
          newState.myQuizPending = {
            ...newState.myQuizPending,
            [item.item_id]: item,
          };
        }
      });

      newState.hasMyQuizPending =
        Object.keys(newState.myQuizPending).length > 0;

      return newState;
    }
    case EMPTY_MY_QUIZ_PENDING:
      return {
        ...state,
        myQuizPending: {},
        hasMyQuizPending: false,
      };
    case RESET_QUIZ:
      return {
        ...state,
        myQuiz: [],
        myQuizBak: [],
        stimulusQuestionsInQuiz: {},
        stimulusQuestionsInQuizBak: {},
      };
    case SET_NEW_QUIZ:
      return {
        ...state,
        quizTitle,
        quizId,
        isDirty: false,
        activeTab: activeTab || state.activeTab,
        libraryId: libraryId || state.libraryId,
      };
    case SET_ACTIVE_STIMULUS_FILTERS:
      return {
        ...state,
        activeStimulusFilters,
      };

    case SET_LOADED_DATA: {
      return {
        ...state,
        itemsColumns,
        items,
        addAllItemIds,
        courseOutlineConfig,
      };
    }
    case SET_EDIT_ASSESSMENT_PROPS:
      return {
        ...state,
        ...payload,
      };
    case CLOSE_AND_RESET_QUIZ:
      return {
        ...state,
        myQuiz: [],
        myQuizBak: [],
        myQuizPending: {},
        stimulusQuestionsInQuiz: {},
        stimulusQuestionsInQuizBak: {},
        questions: {},
        allStimulusQuestions: {},
        quizTitle: '',
        activeTab: tabIds.BROWSE,
        quizId: null,
        activeStimulusFilters: [],
      };
    case QUIZ_REVIEW_TAB_LOADING:
      return {
        ...state,
        quizReviewTabLoading,
      };
    case SAVE_QUIZ:
      return {
        ...state,
        isDirty: false,
        myQuizBak: state.myQuiz.slice(),
        stimulusQuestionsInQuizBak: { ...state.stimulusQuestionsInQuiz },
      };
    case ROLLBACK_QUIZ:
      return {
        ...state,
        isDirty: false,
        myQuiz: state.myQuizBak.slice(),
        stimulusQuestionsInQuiz: { ...state.stimulusQuestionsInQuizBak },
      };

    case UPDATE_ITEM_USAGE: {
      let updatedQuestions;
      ({
        items: updatedItems,
        questions: updatedQuestions,
        allStimulusQuestions: updatedAllStimulusQuestions,
      } = updateItemsUsedDetail({
        ...pick(state, [
          'items',
          'questions',
          'allStimulusQuestions',
          'quizId',
          'quizTitle',
          'myQuiz',
          'stimulusQuestionsInQuiz',
        ]),
        checkAll: updateItemUsageCheckAll,
        remove: updateItemUsageRemove,
      }));

      return {
        ...state,
        items: updatedItems,
        questions: updatedQuestions,
        allStimulusQuestions: updatedAllStimulusQuestions,
      };
    }
    case SET_LOADING_QUIZ:
      return {
        ...state,
        ...payload,
      };
    case UPDATE_ITEM_ASSIGNEES: {
      const newItems = [...state.items];
      newItems.forEach((item, itemIndex) => {
        // Update the usage of current quiz questions
        const itemUsedInCurrentQuiz = item.used_detail[quizId];
        if (itemUsedInCurrentQuiz) {
          // Seems like the table won't update until `used_detail` is a new object
          newItems[itemIndex].used_detail = {
            ...newItems[itemIndex].used_detail,
            [quizId]: {
              assessment_title: itemUsedInCurrentQuiz.assessment_title,
              in_progress: itemUsedInCurrentQuiz.in_progress || false,
              classes: [...itemUsedInCurrentQuiz.classes, ...updatedAssignees],
            },
          };
        }
      });

      const updatedQuestions = cloneDeep(state.questions);
      Object.keys(updatedQuestions).forEach((questionId) => {
        // Update the usage of current quiz questions
        const itemUsedInCurrentQuiz =
          updatedQuestions[questionId].used_detail[quizId];
        const prevClasses = itemUsedInCurrentQuiz
          ? itemUsedInCurrentQuiz.classes
          : [];

        updatedQuestions[questionId].used_detail = {
          ...updatedQuestions[questionId].used_detail,
          [quizId]: {
            ...itemUsedInCurrentQuiz,
            classes: [...prevClasses, ...updatedAssignees],
          },
        };
      });

      return {
        ...state,
        items: newItems,
        questions: updatedQuestions,
      };
    }
    case SET_TEACHER_SECTIONS:
      return {
        ...state,
        teacherSections,
      };
    case SET_SUBJECT_INFO:
      return {
        ...state,
        subjectInfo,
      };
    // Items Columns state reducer
    case TOGGLE_ITEMS_COLUMNS_VISIBLITY:
      return {
        ...state,
        itemsColumns: produce(state.itemsColumns, (draftItemsColumns) => {
          const currentIsVisible = draftItemsColumns[payload].isVisible;
          draftItemsColumns[payload].isVisible = !currentIsVisible;
          // Push the column to the end of list if it was visible
          if (currentIsVisible) {
            draftItemsColumns[payload].index = 99;
          }
          // Because hiding a column means moving it to the end of list
          // We have to reindex all visible and movable columns
          sortBy(Object.values(draftItemsColumns), 'index')
            .filter((column) => column.isMovable && column.isVisible)
            .forEach((column, index) => {
              column.index = index;
            });

          // Persist column when user changed a column visiblity
          persistFn && persistFn(current(draftItemsColumns));
        }),
      };
    case SET_ITEMS_COLUMNS_VISIBLITY:
      return {
        ...state,
        itemsColumns: produce(state.itemsColumns, (draftItemsColumns) => {
          // Payload is an object with new visiblities
          Object.entries(payload).forEach(([internalName, isVisible]) => {
            draftItemsColumns[internalName].isVisible = isVisible;
            // Push invisible columns to the end of list
            if (!isVisible) {
              draftItemsColumns[internalName].index = 99;
            }
          });

          // Changing visiblity of columns means
          // we have to reindex all visible and movable columns
          sortBy(Object.values(draftItemsColumns), 'index')
            .filter((column) => column.isMovable && column.isVisible)
            .forEach((column, index) => {
              draftItemsColumns[column.value].index = index;
            });

          // Persist column when user changed columns visiblity
          persistFn && persistFn(current(draftItemsColumns));
        }),
      };
    case RESET_ITEMS_COLUMNS_VISIBLITY:
      return {
        ...state,
        itemsColumns: produce(state.itemsColumns, (draftItemsColumns) => {
          // Reset columns to default values for `isVisible` and `index`
          Object.values(draftItemsColumns).forEach((column) => {
            if (column.isMovable) {
              draftItemsColumns[column.value].isVisible = column.isDefault;
              draftItemsColumns[column.value].index = column.originalIndex;
            }
          });
        }),
      };
    case MOVE_ITEMS_COLUMN:
      return {
        ...state,
        itemsColumns: produce(state.itemsColumns, (draftItemsColumns) => {
          const columnIndex = draftItemsColumns[payload.column].index;
          const nextIndex = payload.index;
          // Swap swap column indexes
          const swapColumn = Object.values(draftItemsColumns).find(
            (col) => col.index === nextIndex,
          )['value'];
          draftItemsColumns[swapColumn].index = columnIndex;
          draftItemsColumns[payload.column].index = nextIndex;

          // Persist column when user changed columns order
          persistFn && persistFn(current(draftItemsColumns));
        }),
      };
    default:
      throw Error('action type is not supported');
  }
};
